import React from "react";
import { createRoot } from "react-dom/client";
import { styled } from "@mui/material/styles";
import { Provider } from "react-redux";
import "core-js/stable";
import "regenerator-runtime/runtime";
import "leaflet-geosearch/dist/geosearch.css";

import "./i18n/i18n";
import { store, ViewportProvider } from "./helpers";
import { AppRoutes } from "./routes/AppRoutes";
import * as serviceWorker from "./serviceWorker";
import { SnackbarProvider, MaterialDesignContent } from "notistack";

import "./assets/index.css";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import {
  ThemeProvider as MuiThemeProvider,
  StyledEngineProvider,
} from "@mui/material";
import { theme } from "./helpers/theme";
// import firebase from "firebase/compat";
import firebase from "firebase/compat/app";
// v9 compat packages are API compatible with v8 code
import "firebase/compat/auth";
import "firebase/compat/firestore";

import { TourProvider } from "@reactour/tour";
import useSteps from "./helpers/tutoSteps";
import secureLocalStorage from "react-secure-storage";

const PREFIX = "index";

const classes = {
  error: `${PREFIX}-error`,
  success: `${PREFIX}-success`,
  info: `${PREFIX}-info`,
  warning: `${PREFIX}-warning`,
  customButton: `${PREFIX}-customButton`,
};

const StyledMaterialDesignContent = styled(MaterialDesignContent)(() => ({
  "&.notistack-MuiContent-success": {
    backgroundColor: "#484848 !important",
    color: "#CEB673 !important",
  },
  "&.notistack-MuiContent-error": {
    backgroundColor: "#484848 !important",
    color: "#f55c5c !important",
  },
  "&.notistack-MuiContent-info": {
    backgroundColor: "#484848 !important",
    color: "#a5893e !important",
  },
  "&.notistack-MuiContent-warning": {
    backgroundColor: "#484848 !important",
    color: "#f5925c !important",
  },
}));

if (typeof window !== "undefined") window.store = store;

firebase.initializeApp({
  apiKey: process.env.REACT_APP_API_KEY,
  authDomain: process.env.REACT_APP_AUTH_DOMAIN,
  databaseURL: process.env.REACT_APP_DATABASE_URL,
  projectId: process.env.REACT_APP_PROJECT_ID,
  storageBucket: process.env.REACT_APP_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_APP_ID,
  measurementId: process.env.REACT_APP_MEASUREMENT_ID,
});

// eslint-disable-next-line react/jsx-filename-extension
const App = () => (
  <Provider store={store}>
    <AppRoutes />
  </Provider>
);

const notistackRef = React.createRef();
const onClickDismiss = (key) => () => {
  notistackRef.current.closeSnackbar(key);
};

function ZeApp() {
  const steps = useSteps();

  const NextTutorialButton = (props) => {
    const isLastStep = props.currentStep === props.steps.length - 1;
    const Button = props.Button;
    return (
      <>
        {!isLastStep ? (
          <Button />
        ) : (
          <div
            className="tour-custom-button"
            onClick={() => props.setIsOpen(false)}
          >
            Ok!
          </div>
        )}
      </>
    );
  };

  return (
    <StyledEngineProvider injectFirst>
      {/* // handle new theming */}
      <MuiThemeProvider theme={theme}>
        <SnackbarProvider
          maxSnack={3}
          anchorOrigin={{
            vertical: "top",
            horizontal: "right",
          }}
          ref={notistackRef}
          action={(key) => (
            <IconButton onClick={onClickDismiss(key)} size="large">
              <CloseIcon />
            </IconButton>
          )}
          Components={{
            success: StyledMaterialDesignContent,
            error: StyledMaterialDesignContent,
            info: StyledMaterialDesignContent,
            warning: StyledMaterialDesignContent,
          }}
        >
          <ViewportProvider>
            <TourProvider
              onClickMask={() => null}
              steps={steps}
              padding={0}
              showCloseButton
              className="tour-style tourPopover"
              nextButton={(props) => <NextTutorialButton {...props} />}
              beforeClose={() => {
                if (
                  secureLocalStorage &&
                  !secureLocalStorage.getItem("webviewTour")
                ) {
                  secureLocalStorage.setItem("webviewTour", true);
                }
              }}
              styles={{
                highlightedArea: (base, { x, y, width, height }) => {
                  return {
                    ...base,
                    display: "block",
                    stroke: "#ffffff",
                    strokeWidth: 2,
                    width: width,
                    height: height,
                    x: x,
                    y: y,
                    pointerEvents: "none",
                  };
                },
                dot: (base, myDot) => {
                  return {
                    ...base,
                    background: myDot.current && theme.palette.primary.main,
                  };
                },
                badge: (base) => {
                  return {
                    ...base,
                    display: "none",
                  };
                },
                close: (base) => {
                  return {
                    ...base,
                    top: 14,
                    right: 14,
                    color: theme.palette.primary.main,
                  };
                },
              }}
            >
              <App />
            </TourProvider>
          </ViewportProvider>
        </SnackbarProvider>
      </MuiThemeProvider>
    </StyledEngineProvider>
  );
}

const container = document.getElementById("root");
const root = createRoot(container);
root.render(<ZeApp />);

// ReactDOM.render(
//   <StyledZeApp />
//   , document.getElementById('root'));

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: https://bit.ly/CRA-PWA
serviceWorker.unregister();
